import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";

const LatinTerm = ({ children }) => (
  <span className="italic font-medium text-coral relative">{children}</span>
);

const Manifesto = () => {
  const { t } = useLang();
  const M = t.manifesto;

  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-28 md:py-36 bg-[#F8F4EE] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #0A192F 0 1px, transparent 1px 22px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-coral/10 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-navy/5 blur-3xl pointer-events-none"
      />

      <div className="container-vel relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="overline mb-5" data-testid="manifesto-overline">
              {M.overline}
            </div>
            <h2
              data-testid="manifesto-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy font-light leading-[1.05] tracking-tight"
            >
              {M.headlinePre}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 italic font-normal">
                  {M.headlineHighlight}
                </span>
                <span className="absolute left-0 right-0 bottom-1 h-2 bg-coral/30 -z-0" />
              </span>
              {M.headlinePost}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 lg:pl-10 lg:border-l border-navy/10"
          >
            <p
              data-testid="manifesto-lede"
              className="font-heading text-xl sm:text-2xl text-navy/85 leading-snug italic font-light"
            >
              {M.lede}
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 font-body text-base sm:text-lg text-ink leading-[1.75]">
            <p data-testid="manifesto-p1">{M.p1}</p>
            <p data-testid="manifesto-p2">
              {M.p2Pre} <LatinTerm>{M.p2Iter}</LatinTerm> {M.p2Mid}{" "}
              <LatinTerm>{M.p2Profectus}</LatinTerm>
              {M.p2Post}
            </p>
            <p data-testid="manifesto-p3">
              {M.p3Pre} <LatinTerm>{M.p3Incrementum}</LatinTerm> {M.p3Post}
            </p>

            <div
              data-testid="manifesto-closing"
              className="mt-10 pt-8 border-t border-navy/15"
            >
              <p className="font-heading text-2xl sm:text-3xl lg:text-4xl text-navy leading-[1.15] font-light">
                {M.closingLine1}
                <br />
                <span className="italic text-coral">{M.closingHighlight}</span>{" "}
                {M.closingPost}
              </p>
            </div>
          </div>

          <div className="lg:col-span-4 lg:col-start-1 lg:row-start-1 order-first lg:order-none">
            <div className="lg:sticky lg:top-32 space-y-5 border-l-2 border-coral/40 pl-6">
              {M.terms.map((term) => (
                <div
                  key={term.word}
                  data-testid={`manifesto-term-${term.word.toLowerCase()}`}
                >
                  <div className="font-heading text-2xl text-navy italic font-medium">
                    {term.word}
                  </div>
                  <div className="font-body text-xs tracking-[0.18em] uppercase text-ink-soft mt-1">
                    {term.meaning}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Manifesto;
